import { Header } from "@/components/sven/Header";
import { Footer } from "@/components/sven/Footer";
import { PageHero, BookCta } from "@/components/sven/PageHero";
import { Reveal } from "@/components/sven/Reveal";
import { StyleQuizTrigger } from "@/components/sven/StyleQuizModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

/*
 * STYLE ENCYCLOPEDIA PAGE — converted from the standalone HTML guide.
 * The interactive "Ask Sven" quiz now lives in StyleQuizModal.jsx so it can
 * pop up from anywhere on the site; this page links out to it rather than
 * duplicating the form.
 */
const STYLE_EDIT_CARDS = [
  { title: "Classic, without looking dated", text: "A clean single-breasted jacket, a strong trouser, good cloth and disciplined proportions. The point is not to look traditional; it is to make the clothes work for more than one season." },
  { title: "The double-breasted question", text: "A double-breasted suit changes the architecture of the body. The closure, button stance, lapel and trouser all matter. Done well, it can feel remarkably current." },
  { title: "Relaxed tailoring", text: "Less padding, softer construction, more room through the jacket and trouser. The garment remains tailored, but it stops behaving like a uniform." },
  { title: "When the occasion matters", text: "A tuxedo, dinner jacket, three-piece or ceremonial garment deserves its own logic. Formal dressing becomes easier when the clothes understand the room you are entering." },
];

const SILHOUETTE_CARDS = [
  { title: "Single-Breasted", text: "The starting point for most wardrobes. Two-button, one-button, notch or peak — the details determine whether it feels businesslike, relaxed, evening-appropriate or distinctly fashion-led.", tags: "bespoke suit · tailored suit · custom suit" },
  { title: "Double-Breasted", text: "More fabric across the chest, a stronger visual centre and a natural relationship with a wider lapel and higher-waisted trouser. It can be formal, relaxed or decidedly modern.", tags: "double-breasted suit · DB suit · peak lapel" },
  { title: "The Trouser", text: "The trouser is often the part people notice least and the part that changes a silhouette most. Rise, pleats, waistband, thigh, hem and break all deserve attention.", tags: "tailored trousers · pleated trousers · wide-leg trousers" },
  { title: "Sports Jacket", text: "A jacket that does not need matching trousers. It is one of the easiest ways to make tailoring useful outside the suit — with denim, chinos, wool trousers or linen.", tags: "sports jacket · tailored jacket · odd jacket" },
  { title: "Three-Piece", text: "The waistcoat changes the visual line of the outfit and gives a traditional suit another layer of depth. Especially useful for weddings, receptions and formal day dressing.", tags: "three-piece suit · wedding suit · waistcoat" },
  { title: "Tuxedo & Dinner Jacket", text: "Black tie has its own vocabulary: satin, clean lines, a formal shirt, appropriate trousers and the right degree of restraint. The goal is elegance, not decoration.", tags: "tuxedo · dinner jacket · black tie" },
];

const FABRIC_CARDS = [
  { title: "Linen", text: "Linen brings movement and texture. Its natural irregularity is part of its appeal. Cut it generously and it becomes relaxed; give it more structure and it can become surprisingly formal.", tags: "linen suit · linen tailoring · summer tailoring" },
  { title: "Wool", text: "Wool remains the backbone of tailoring because it can hold a clean line while remaining comfortable. Weight, weave and finish matter as much as the fibre itself.", tags: "wool suit · merino · suiting cloth" },
  { title: "Italian Character", text: "Softer shoulders, fluid cloth, expressive texture and an ease through the body. Italian-inspired tailoring is less about a fixed look than a relationship between structure and movement.", tags: "Italian tailoring · soft tailoring · relaxed suit" },
  { title: "British Structure", text: "Clearer shoulders, firmer cloth and a more defined silhouette. British-inspired tailoring can give a suit presence without relying on excessive detail.", tags: "British tailoring · structured suit · classic tailoring" },
];

const WARDROBE_CARDS = [
  { num: "01", title: "The Navy Foundation", text: "A clean, versatile suit that can move between work, dinner, weddings and travel depending on the shirt and shoes." },
  { num: "02", title: "The Warm-Weather Suit", text: "Linen, tropical wool or a breathable blend. The cut determines whether it feels casual, polished or evening-ready." },
  { num: "03", title: "The Statement Jacket", text: "Double-breasted, textured, patterned or simply cut differently. One piece that gives the wardrobe a point of view." },
  { num: "04", title: "The Trouser You Keep Wearing", text: "The pair that works with a shirt, polo, knit, sports jacket or blazer. This is where a wardrobe starts becoming flexible." },
];

const QUESTIONS_FAQ = [
  { q: "Why a double-breasted suit?", a: "Because sometimes the point is not more formality. It is a different proportion: more chest, a stronger lapel and a more deliberate relationship between jacket and trouser." },
  { q: "Can a linen suit be formal?", a: "Yes — depending on the cloth, colour, construction and styling. Linen is relaxed by nature, but the rest of the garment decides how far that relaxation goes." },
  { q: "Do wider trousers actually work?", a: "They can. The important question is balance: rise, jacket length, hem, shoulder and the amount of volume through the leg need to belong to the same silhouette." },
  { q: "What makes a suit look expensive?", a: "Not one detail. Clean shoulders, controlled proportions, good cloth, a convincing drape and finishing that does not need to shout for attention." },
  { q: "Italian or British?", a: "Think of them as starting points rather than rules. You can borrow softness from one tradition and structure from another until the garment makes sense for you." },
  { q: "What if I do not know what suits me?", a: "That is exactly what a consultation is for. Bring the references you save, the clothes you already wear and the occasions you dress for. The rest can be worked out together." },
];

const Quote = ({ children }) => (
  <Reveal>
    <blockquote className="border-l-2 border-[#c5a059] pl-6 py-1 my-12 max-w-2xl">
      <p className="font-accent italic text-xl sm:text-2xl leading-snug text-[#141414]">{children}</p>
    </blockquote>
  </Reveal>
);

const Card = ({ title, text, tags }) => (
  <div className="p-7 border border-[#e7e2da] bg-white hover:border-[#c5a059] transition-colors duration-300">
    <h3 className="font-display text-lg text-[#141414]">{title}</h3>
    <p className="mt-2.5 text-sm font-light leading-relaxed text-[#57534e]">{text}</p>
    {tags && <p className="mt-4 font-mono-ov text-[10px] tracking-[0.1em] text-[#99732b]/80">{tags}</p>}
  </div>
);

export default function StyleEncyclopediaPage() {
  return (
    <main data-testid="style-encyclopedia-page">
      <Header />

      <PageHero overline="Sven Suits · The Style Encyclopedia" title="Dress well." accent="Know why.">
        <p className="mt-6 max-w-xl text-sm sm:text-base font-light leading-relaxed text-white/60">
          A practical guide to the decisions behind a great wardrobe — from the difference between
          a soft Italian jacket and a structured British one, to whether your next suit should be
          linen, wool, double-breasted or something else entirely.
        </p>
        <div className="mt-8">
          <StyleQuizTrigger label="Take the Style Quiz" testId="hero-style-quiz-trigger" />
        </div>
      </PageHero>

      {/* The Style Edit */}
      <section className="bg-[#faf8f5] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="overline-label mb-5">The Style Edit</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-[#141414] max-w-2xl">
              Start with the clothes <em className="font-accent text-[#99732b]">you are drawn to.</em>
            </h2>
            <p className="mt-6 max-w-2xl text-sm sm:text-base font-light leading-relaxed text-[#57534e]">
              Most people know what they like before they know the name for it. This is where those
              instincts become useful — learn the language of tailoring, then decide which parts
              belong in your wardrobe.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4 mt-12">
            {STYLE_EDIT_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={0.06 * i}>
                <Card {...c} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Silhouette & Construction */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="overline-label mb-5">Silhouette & Construction</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-[#141414] max-w-2xl">
              The details change <em className="font-accent text-[#99732b]">the way the whole garment reads.</em>
            </h2>
            <p className="mt-6 max-w-2xl text-sm sm:text-base font-light leading-relaxed text-[#57534e]">
              Fit is not simply whether the jacket closes. Shoulder line, chest, jacket length, lapel
              width, button stance, trouser rise and leg shape work together.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {SILHOUETTE_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={0.05 * i}>
                <Card {...c} />
              </Reveal>
            ))}
          </div>
          <Quote>A good silhouette does not announce every decision. You notice the whole before you notice the details.</Quote>
        </div>
      </section>

      {/* Cloth & Climate */}
      <section className="bg-[#faf8f5] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="overline-label mb-5">Cloth & Climate</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-[#141414] max-w-2xl">
              Choose the cloth <em className="font-accent text-[#99732b]">before you choose the mood.</em>
            </h2>
            <p className="mt-6 max-w-2xl text-sm sm:text-base font-light leading-relaxed text-[#57534e]">
              Fabric determines how a garment hangs, breathes, wrinkles, photographs and ages. In
              Mumbai, climate is not a footnote — it is part of the design brief.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4 mt-12">
            {FABRIC_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={0.06 * i}>
                <Card {...c} />
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <h3 className="font-display text-xl sm:text-2xl text-[#141414] mt-16 max-w-2xl">
              Why linen works differently in Mumbai.
            </h3>
            <p className="mt-4 max-w-2xl text-sm sm:text-base font-light leading-relaxed text-[#57534e]">
              A fabric that looks beautiful in a colder city can feel unnecessarily heavy here.
              Linen, lighter wool and breathable blends allow the jacket to retain a tailored shape
              while giving the body more room to breathe.
            </p>
          </Reveal>
          <Quote>Fabric sets the atmosphere. Cut decides what you do with it.</Quote>
        </div>
      </section>

      {/* The Wardrobe */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="overline-label mb-5">The Wardrobe</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-[#141414] max-w-2xl">
              Build around <em className="font-accent text-[#99732b]">combinations, not occasions.</em>
            </h2>
            <p className="mt-6 max-w-2xl text-sm sm:text-base font-light leading-relaxed text-[#57534e]">
              Instead of collecting isolated outfits, think in systems: one strong navy suit, a
              lighter seasonal cloth, a pair of trousers with enough character to stand alone, a
              sports jacket, and formalwear that is actually yours.
            </p>
          </Reveal>
          <div className="mt-10">
            {WARDROBE_CARDS.map((v, i) => (
              <Reveal key={v.num} delay={0.06 * i}>
                <div className="flex gap-6 py-7 border-t border-[#e7e2da] last:border-b">
                  <span className="font-accent italic text-2xl text-[#c5a059] leading-none pt-1">{v.num}</span>
                  <div>
                    <h3 className="font-display text-lg text-[#141414]">{v.title}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-[#57534e]">{v.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Questions Worth Asking */}
      <section className="bg-[#faf8f5] py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="overline-label mb-5">Questions Worth Asking</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-[#141414] mb-10">
              Before you order <em className="font-accent text-[#99732b]">the next garment.</em>
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {QUESTIONS_FAQ.map((f, i) => (
                <AccordionItem key={i} value={`q-${i}`} data-testid={`style-faq-item-${i}`} className="border-[#e7e2da]">
                  <AccordionTrigger className="font-display text-base sm:text-lg text-[#141414] hover:text-[#99732b] hover:no-underline text-left py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm font-light leading-relaxed text-[#57534e] pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Ask Sven */}
      <section id="ask-sven" className="bg-[#0b0b0c] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="overline-label mb-5">Ask Sven</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-[#faf8f5] max-w-2xl mx-auto">
              Tell us what <em className="font-accent text-[#dfcca4]">you are trying to solve.</em>
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-sm font-light leading-relaxed text-white/55">
              You do not need to know the name of the style. Tell us what you like, where you'll
              wear it, and what you already own — we'll review it before suggesting a direction.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <StyleQuizTrigger testId="footer-style-quiz-trigger" />
              <BookCta testId="style-encyclopedia-book-cta" />
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
